import { useRef, useState } from 'react';
import { Plus, WarningCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { scanPackSize, scanPiecePrice, scanTotal } from '@/lib/scanQuantity';
import { addConfirmedScanQuantity } from '@/lib/scanItems';
import { scanDiscountedTotal } from '@/lib/scanDiscount';
import { fmt } from '@/lib/api';

export const ScanRepeatDialog = ({ item, initialQuantity = 1, replacingRow, onConfirm, onCancel, onReturnFocus }) => {
  const size = scanPackSize(item);
  const [unit, setUnit] = useState(size > 1 ? 'packs' : 'pieces');
  const [quantity, setQuantity] = useState(String(initialQuantity));
  const [scanWarning, setScanWarning] = useState(false);
  const cancelRef = useRef(null);
  const submitted = useRef(false);
  const burst = useRef({ count: 0, at: 0, before: String(initialQuantity) });
  const next = quantity.trim() && !scanWarning ? addConfirmedScanQuantity(item, quantity, unit) : null;
  const packLabel = item.eenheid === 'pallet' ? 'Pallets' : 'Pakken';
  const pieceLabel = item.eenheid_label && !['st', 'stuks'].includes(item.eenheid_label.toLowerCase()) ? item.eenheid_label : 'stuks';
  const submit = e => {
    e.preventDefault();
    if (!next || submitted.current) return;
    submitted.current = true;
    if (!onConfirm(Number(quantity), unit)) submitted.current = false;
  };
  return <Dialog open onOpenChange={open => { if (!open) onCancel(); }}>
    <DialogContent overlayClassName="!bg-transparent" className="w-[calc(100%-2rem)] max-w-md max-h-[90dvh] overflow-y-auto rounded-md !bg-white text-gray-900 shadow-xl" data-testid="scan-repeat-dialog"
      onOpenAutoFocus={e => { e.preventDefault(); cancelRef.current?.focus(); }}
      onKeyDownCapture={e => {
        const now = performance.now();
        if (e.key.length === 1) {
          const fast = now - burst.current.at < 40;
          burst.current = { count: fast ? burst.current.count + 1 : 1, at: now, before: fast ? burst.current.before : quantity };
        }
        // Scanner keystrokes must not accidentally submit or cancel this confirmation.
        if (e.key === 'Enter' && burst.current.count >= 4 && now - burst.current.at < 80) {
          e.preventDefault(); e.stopPropagation(); setScanWarning(true); setQuantity(burst.current.before);
          burst.current = { count: 0, at: 0, before: burst.current.before };
        }
      }}
      onCloseAutoFocus={e => { e.preventDefault(); onReturnFocus?.(); }}>
      <div className="flex items-start gap-3 pr-5">
        <WarningCircle size={24} className="text-amber-600 shrink-0" />
        <div className="min-w-0"><DialogTitle data-testid="scan-repeat-title">Artikel al gescand</DialogTitle>
          <DialogDescription className="mt-1 break-words" data-testid="scan-repeat-description">{item.omschrijving}{item.variant_label ? ` · ${item.variant_label}` : ''}</DialogDescription>
          <div className="mt-1 text-xs text-gray-500 break-all" data-testid="scan-repeat-code">{item.variant_code || item.art_code}</div>
        </div>
      </div>
      <div className="flex flex-wrap justify-between gap-2 border-y border-gray-200 py-3 text-sm">
        <span className="text-gray-500">Al in de lijst</span><strong data-testid="scan-repeat-current-total">{scanTotal(item)} {pieceLabel}</strong>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="scan-repeat-form">
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
          <label className="min-w-0"><span className="form-label">Hoeveel toevoegen?</span>
            <input type="text" inputMode="numeric" value={quantity} autoComplete="off" aria-invalid={!next}
              className="field-input w-full" data-testid="scan-repeat-quantity" onFocus={e => e.target.select()}
              onChange={e => { setScanWarning(false); setQuantity(e.target.value.replace(/^0+(?=\d)/, '')); }} /></label>
          <label className="min-w-0"><span className="form-label">Eenheid</span>
            <select className="field-input w-full" value={unit} onChange={e => setUnit(e.target.value)} data-testid="scan-repeat-unit">
              <option value="pieces">{pieceLabel === 'stuks' ? 'Stuks' : pieceLabel}</option>
              {size > 1 && <option value="packs">{packLabel} ({size} {pieceLabel})</option>}
            </select></label>
        </div>
        {scanWarning ? <p role="alert" className="text-xs text-amber-800" data-testid="scan-repeat-scan-warning">Kies eerst hoeveel je wilt toevoegen. Deze nieuwe scan is niet verwerkt.</p>
          : !next && <p role="alert" className="text-xs text-red-700" data-testid="scan-repeat-error">Vul een heel aantal vanaf 1 in.</p>}
        {replacingRow && <p className="text-xs text-gray-500" data-testid="scan-repeat-merge-note">De geselecteerde invoerregel wordt met deze regel samengevoegd.</p>}
        <div className="space-y-2 border-t border-gray-200 pt-3 text-sm" aria-live="polite">
          <div className="flex justify-between gap-3"><span>Nieuw totaal</span><strong data-testid="scan-repeat-new-total">{next ? `${scanTotal(next)} ${pieceLabel}` : '—'}</strong></div>
          <div className="flex justify-between gap-3"><span>Regelbedrag excl. btw</span><strong data-testid="scan-repeat-new-amount">{next ? fmt(scanDiscountedTotal(next, scanTotal(next) * scanPiecePrice(next))) : '—'}</strong></div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button ref={cancelRef} type="button" onClick={onCancel} className="btn btn-secondary btn-sm" data-testid="scan-repeat-cancel">Niet toevoegen</button>
          <button type="submit" disabled={!next} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="scan-repeat-confirm"><Plus size={15} />Toevoegen</button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
};